import { Building2, CalendarCheck, CreditCard, Palette, Users } from 'lucide-react';
import type { Section } from './ChecklistBoard';

/**
 * Phase 1 manual smoke test — the 12 launch-readiness checks from
 * docs/phase-1-manual-smoke-test.md, written as a tester (Ronna / Johannes /
 * Chris) would work through a real cleaning business.
 *
 * Scenario ids are prefixed `smoke-` so they never collide with the UAT
 * checklist's ids in the shared uat_checklist_results row.
 */
export const SMOKE_SECTIONS: Section[] = [
  {
    id: 'smoke-onboarding',
    title: 'Onboarding & public site',
    description: 'A new cleaning business can sign up and gets its own branded public site.',
    icon: Building2,
    scenarios: [
      { id: 'smoke-1', title: 'Create a new cleaning business', detail: 'Sign up `Ronna Test Cleaning`, sign in as the owner and confirm the admin dashboard loads — and shows no other company’s data.' },
      { id: 'smoke-2', title: 'Check the public website for the new business', detail: 'The tenant subdomain shows `Ronna Test Cleaning` details (not founder/default copy); Services and About pages load.' },
      { id: 'smoke-3', title: 'Add a service in admin and see it publicly', detail: 'Add `Smoke Test Standard Clean` in admin, save it, and confirm it appears on the public Services page.' },
    ],
  },
  {
    id: 'smoke-booking',
    title: 'Bookings, emails & customer access',
    description: 'A customer can enquire/book and the right people get the right emails.',
    icon: CalendarCheck,
    scenarios: [
      { id: 'smoke-4', title: 'Submit a booking/enquiry as a customer', detail: 'As `Ada Booker`, submit the public booking/enquiry, see a success message, then confirm it appears in the owner’s admin.' },
      { id: 'smoke-5', title: 'Check owner and customer emails', detail: 'Owner gets a new enquiry/booking notification and the customer gets a confirmation — both branded as `Ronna Test Cleaning` with correct links.' },
      { id: 'smoke-6', title: 'Customer magic-link dashboard', detail: 'Ada requests dashboard access, opens the magic-link email, and sees only her own enquiry/booking.' },
    ],
  },
  {
    id: 'smoke-team',
    title: 'Team & job assignment',
    description: 'Staff can be invited with the right permissions and notified of jobs.',
    icon: Users,
    scenarios: [
      { id: 'smoke-7', title: 'Invite a cleaner/team member', detail: 'Invite `Casey Cleaner`, complete the invite/OTP, and confirm Casey cannot edit owner-only settings while the owner can.' },
      { id: 'smoke-8', title: 'Assign a job to a cleaner', detail: 'Assign a test job to Casey, confirm it saves and Casey receives an assignment email referencing the right business/job.' },
    ],
  },
  {
    id: 'smoke-payments',
    title: 'Payments & invoicing',
    description: 'The correct tenant payment link is used and invoices send correctly.',
    icon: CreditCard,
    scenarios: [
      { id: 'smoke-9', title: 'Payment link in emails', detail: 'Save the tenant’s own Stripe Payment Link in admin and confirm the customer email contains that link — not another business’s.' },
      { id: 'smoke-10', title: 'Create and send an invoice', detail: 'Create an invoice for `Ada Booker` with a line item, save it, download the PDF and send it — email/PDF show the correct business and customer.' },
    ],
  },
  {
    id: 'smoke-customisation',
    title: 'Customisation & service area',
    description: 'Template switching is visible and address/service-radius rules apply.',
    icon: Palette,
    scenarios: [
      { id: 'smoke-11', title: 'Change the website template/theme', detail: 'Switch template/theme in admin and confirm the public homepage layout/colour/typography visibly changes and stays usable.' },
      { id: 'smoke-12', title: 'Address search and service radius', detail: 'In a booking, an in-area address autocompletes and is accepted while an out-of-area address is rejected/not serviceable.' },
    ],
  },
];
